<template>
  <Preloader
    v-if="loading"
  />
  <VRow v-else class="h-100">
    <VCol cols="12" class="d-flex flex-column">
      <PageTitle
        no-border
        tag="h1"
        level="2"
        class="my-5"
      >
        <span class="d-none d-md-inline font-weight-light">
          {{ $t('vacancy') }}: </span>{{ vacancyTitle }}
      </PageTitle>
      <VDivider />
      <VacancyAiQuestionsForm
        v-if="vacancy"
        :vacancy="vacancy"
        :questions="questions"
        :categories="categories"
        :current-interviewer-id="currentInterviewerId"
        :ai-interviewer="aiInterviewer"
        :redacting-ai="redactingAi"
        :avatars="avatars"
        @fetchById="fetchById"
        @setAiInterviewIsCompiled="setAiInterviewIsCompiled"
        @next="next"
      >
        <template v-slot:backButton="{ disabled }">
          <VBtn
            color="success"
            outlined
            class="mr-auto mr-md-2"
            :disabled="disabled"
            elevation="0"
            @click="back"
          >
            {{ $t('goBack') }}
          </VBtn>
        </template>
        <template v-slot:saveButton="{ disabled, submit }">
          <VBtn
            color="success"
            type="submit"
            class="mr-auto mr-md-2"
            :disabled="disabled"
            elevation="0"
            @click="submit"
          >
            {{ $t('save') }}
          </VBtn>
        </template>
        <template v-slot:publishButton="{ disabled, submit }">
          <VBtn
            color="success"
            type="submit"
            :disabled="disabled"
            elevation="0"
            @click="next(submit)"
          >
            {{ $t('publish') }}
          </VBtn>
        </template>
      </VacancyAiQuestionsForm>
    </VCol>
  </VRow>
</template>

<script>
import VacancyAiQuestionsForm from '@/components/Vacancy/VacancyAiQuestionsForm.vue';
import {
  ref, computed, toRefs, onMounted, watch,
} from '@vue/composition-api';
import PageTitle from '@/components/PageTitle.vue';
import useVacancies from '@/compositions/useVacancies';
import Preloader from '@/components/Preloader.vue';
import InterviewVideoCategoryVideo from '@/orm/models/Interview/InterviewVideoCategoryVideo';
import InterviewVideoQuestionVideo from '@/orm/models/Interview/InterviewVideoQuestionVideo';
import useInterviewer from '@/compositions/useInterviewer';
import useAvatars from '@/compositions/useAvatars';

export default {
  name: 'VacancyAiQuestionsEdit',
  metaInfo() {
    return {
      title: this.$t('step'),
    };
  },
  components: {
    VacancyAiQuestionsForm,
    PageTitle,
    Preloader,
  },
  props: {
    vacancyId: {
      type: Number,
      required: true,
    },
    redactingAi: {
      type: Boolean,
      required: true,
    },
  },
  setup(props, {
    root: {
      $router,
    },
  }) {
    const { interviewers, fetchInterviewers } = useInterviewer();
    const { vacancyId } = toRefs(props);
    const {
      getVacancyById, fetchById,
    } = useVacancies();
    const { avatars, getAvatars } = useAvatars();
    const loading = ref(false);
    const aiInterviewIsCompiled = ref(false);

    const vacancy = computed(() => getVacancyById(vacancyId.value));
    const currentInterviewerId = computed(() => (interviewers.value.length ? interviewers.value[0].id : 0));
    const questions = computed(() => InterviewVideoQuestionVideo.query().withAllRecursive().all());
    const categories = computed(() => InterviewVideoCategoryVideo.query().where('depth', 0).withAllRecursive().all());
    const aiInterviewer = computed(() => vacancy.value?.avatar);
    const vacancyTitle = computed(() => vacancy.value?.title);

    function goToVacancy() {
      $router.push({
        name: 'vacancy',
        params: {
          id: vacancyId.value,
        },
      });
    }

    function setAiInterviewIsCompiled() {
      aiInterviewIsCompiled.value = true;
    }

    async function next(submit) {
      submit();
    }

    async function back() {
      goToVacancy();
    }

    watch(aiInterviewIsCompiled, () => {
      if (aiInterviewIsCompiled.value && vacancy.value.isAiInterviewCompiled) {
        goToVacancy();
      }
    });

    onMounted(async () => {
      loading.value = true;
      if (!vacancy.value) {
        await fetchById(vacancyId.value);
      }
      if (!interviewers.value.length) {
        await fetchInterviewers();
      }
      if (!avatars.value) {
        await getAvatars();
      }
      loading.value = false;
    });

    return {
      categories,
      questions,
      vacancy,
      vacancyTitle,
      currentInterviewerId,
      back,
      next,
      loading,
      fetchById,
      setAiInterviewIsCompiled,
      aiInterviewer,
      avatars,
    };
  },
};
</script>

<style lang="scss" scoped>
.q-row {
  background: #fff;
  margin-bottom: 5px;
}

.h-100 {
  height: 100%;
}
</style>

<i18n locale="en" lang="yaml">
  step: "Interview questions list"
  next: Next
  lostInfoWarning: Changes not saved will be lost. Are you sure?
  continue: Continue editing
  goBack: Go back
  returnToMaster: Back to the Template Selection wizard
  returnToVacancy: Go back to the job
  congrats: You have completed question selection.
  anyTime: You can delete a question at any time, swap questions or add a new question.
  pleaseWaitAiLoaderText: 'Artificial intelligence is in the process of selecting questions for your job.
  The expected wait time is 3-5 minutes. Please hold on. If necessary, you can go to other sections.
  The job will be available in the jobs section after completion of the preparation process.'
  pleaseWaitAiLoaderTextVoice: 'Artificial intelligence is in the process of voicing questions for your job.
  We expect it to take 3-5 minutes, please wait... If necessary, you can go to other sections.
  The job will be available in the jobs section after completion of the preparation process'
  pleaseWaitAiLoaderTextRecompiling: 'Artificial intelligence is in the process of voicing additional questions for your job.
  The expected time is 3-5 minutes, please wait… If necessary, you can go to other sections.
  The job will be available in the jobs section after completing the preparation process.'
  pleaseWaitAiLoaderTextCopy: 'The job is in the process of copying. The expected time is 1-3 minutes. Please wait…'
  save: Save
  vacancy: Job
  publish: Publish
</i18n>

<i18n locale="ru" lang="yaml">
  step: "Список вопросов для интервью"
  next: Далее
  lostInfoWarning: Все изменения будут потеряны. Вы уверены?
  continue: Продолжить редактирование
  goBack: Назад
  returnToMaster: Вернуться в мастер выбора шаблона
  returnToVacancy: Вернуться к вакансии
  congrats: Ура! ИИ завершил подборку вопросов для вашей вакансии
  anyTime: В любой момент вы можете удалить вопрос, поменять вопросы местами или добавить новый.
  pleaseWaitAiLoaderText: 'Искусственный интеллект в процессе подбора вопросов для вашей вакансии.
  Ожидаемое время 3-5 минут, пожалуйста, подождите… При необходимости вы можете перейти в другие разделы.
  Вакансия будет доступна в разделе вакансий после завершения процесса подготовки.'
  pleaseWaitAiLoaderTextVoice: 'Искусственный интеллект в процессе озвучивания вопросов для вашей вакансии.
  Ожидаемое время 3-5 минут, пожалуйста, подождите… При необходимости вы можете перейти в другие разделы.
  Вакансия будет доступна в разделе вакансий после завершения процесса подготовки.'
  pleaseWaitAiLoaderTextRecompiling: 'Искусственный интеллект в процессе озвучивания дополнительных вопросов для вашей вакансии.
  Ожидаемое время 3-5 минут, пожалуйста, подождите… При необходимости Вы можете перейти в другие разделы.
  Вакансия будет доступна в разделе вакансий, после завершения процесса подготовки.'
  pleaseWaitAiLoaderTextCopy: 'Вакансия в процессе копирования. Ожидаемое время 1-3 минуты. Пожалуйста, подождите…'
  save: Сохранить
  vacancy: Вакансия
  publish: Опубликовать
</i18n>
